import { useState } from "react";
import { z } from "zod";
import { useTableStore } from "@/store/tableStore";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useStoreManagementStore } from "@/store/storeManagement";

interface AddTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const tableSchema = z.object({
  name: z.string().min(1, "Table name is required"),
  number: z.string().refine((val) => {
    const num = parseInt(val);
    return !isNaN(num) && num > 0;
  }, "Table number must be a positive integer"),
});

export function AddTableDialog({ open, onOpenChange }: AddTableDialogProps) {
  const { tables, createTable } = useTableStore();
  const { selectedStoreId } = useStoreManagementStore();
  const { toast } = useToast();

  const [name, setName] = useState("");
  const [tableNumber, setTableNumber] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setName("");
    setTableNumber("");
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!selectedStoreId) {
      toast({
        title: "No Store Selected",
        description: "Please select a store before adding a table.",
        variant: "destructive",
      });
      return;
    }

    try {
      const validData = tableSchema.parse({ name, number: tableNumber });
      const tableNumberInt = parseInt(tableNumber);

      // Check if table number is already in use for this store
      const existingTable = tables.find((t) => t.number === tableNumberInt);
      if (existingTable) {
        setErrors({
          number: "This table number is already in use in this store",
        });
        return;
      }

      setIsSubmitting(true);

      // Add the table
      await createTable({
        store_id: selectedStoreId,
        name,
        number: tableNumberInt,
        assigned_staff_id: null,
        daily_scans: 0,
        total_scans: 0,
      });

      toast({
        title: "Table Created",
        description: `Table ${tableNumberInt} has been added successfully.`,
      });

      handleClose();
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path) {
            fieldErrors[err.path[0]] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          title: "Error",
          description: "Failed to create table. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Table</DialogTitle>
          <DialogDescription>
            Create a new table with a QR code for your store
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="tableNumber">Table Number *</Label>
            <Input
              id="tableNumber"
              type="number"
              min="1"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              placeholder="Enter table number"
              className={errors.number ? "border-destructive" : ""}
            />
            {errors.number && (
              <p className="text-sm text-destructive">{errors.number}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Table Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Patio Table 1"
              className={errors.name ? "border-destructive" : ""}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Table"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
